<template>
  <div class="group-runs-table">
    <v-skeleton-loader v-if="loading" type="list-item-two-line@3" />

    <v-card v-else-if="!batches.length" outlined class="group-runs-table__empty">
      <v-icon size="48" color="grey lighten-1">mdi-tray</v-icon>
      <div class="subtitle-1 grey--text text--darken-1 mt-2">No runs yet</div>
      <v-btn small color="primary" depressed class="mt-3" @click="$emit('run-group')">
        <v-icon small left>mdi-play</v-icon>
        Re-run group
      </v-btn>
    </v-card>

    <v-expansion-panels v-else v-model="openPanels" multiple flat class="group-runs-table__batches">
      <v-expansion-panel
        v-for="batch in batches"
        :key="batch.batch"
        class="group-runs-table__batch"
        :class="{ 'group-runs-table__batch--complete': batch.complete }"
      >
        <v-expansion-panel-header class="group-runs-table__batch-header">
          <div class="d-flex align-center flex-wrap group-runs-table__batch-title">
            <strong class="mr-2">{{ batch.batch || 'No batch' }}</strong>
            <span class="caption grey--text mr-3">{{ formatDate(batch.started_at) }}</span>
            <v-chip
              v-for="count in batch.status_counts"
              :key="count.status"
              x-small
              label
              :color="count.color"
              dark
              class="mr-1 group-runs-table__count"
            >
              {{ count.label }} {{ count.n }}
            </v-chip>
            <v-chip
              v-if="batch.complete"
              x-small
              outlined
              :color="batch.snapshot_id ? 'success' : 'grey'"
              class="ml-2"
              :title="
                batch.snapshot_id ? 'Snapshot saved when the batch completed' : 'Batch complete'
              "
            >
              <v-icon x-small left>{{
                batch.snapshot_id ? 'mdi-camera-outline' : 'mdi-check'
              }}</v-icon>
              {{ batch.snapshot_id ? 'snapshot' : 'complete' }}
            </v-chip>
            <v-chip v-else x-small outlined color="primary" class="ml-2">in progress</v-chip>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-simple-table dense class="group-runs-table__table">
            <template #default>
              <thead>
                <tr>
                  <th>Video</th>
                  <th>Status</th>
                  <th>Versions</th>
                  <th>Result</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th class="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="run in batch.runs" :key="runIdOf(run)" class="group-runs-table__run">
                  <td>
                    <span class="group-runs-table__source">{{ sourceLabel(run) }}</span>
                  </td>
                  <td><RunStatusChip :status="run.status" x-small /></td>
                  <td><RunVersionChips :run="run" :source-type="run.source_type" dense /></td>
                  <td>{{ resultOf(run) }}</td>
                  <td class="text-no-wrap">{{ formatDate(run.started_at || run.enqueued_at) }}</td>
                  <td class="text-no-wrap">{{ durationOf(run) }}</td>
                  <td class="text-right text-no-wrap">
                    <v-btn
                      icon
                      x-small
                      title="Open run"
                      :disabled="!sourceRoute(run)"
                      @click="openRun(run)"
                    >
                      <v-icon small>mdi-open-in-new</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="isQueued(run)"
                      icon
                      x-small
                      title="Open analysis queue"
                      to="/analysis-queue"
                    >
                      <v-icon small>mdi-tray-full</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="isLive(run)"
                      icon
                      x-small
                      color="warning"
                      title="Cancel run"
                      class="group-runs-table__cancel"
                      @click="$emit('cancel', run)"
                    >
                      <v-icon small>mdi-close-circle-outline</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="isSucceeded(run) && !isPinned(run)"
                      icon
                      x-small
                      title="Pin this run for the member"
                      class="group-runs-table__pin"
                      @click="$emit('pin', run)"
                    >
                      <v-icon small>mdi-pin-outline</v-icon>
                    </v-btn>
                    <v-icon
                      v-else-if="isPinned(run)"
                      small
                      color="info"
                      title="Pinned"
                      class="mx-1"
                    >
                      mdi-pin
                    </v-icon>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import RunStatusChip from '@/components/analysis/runs/RunStatusChip.vue';
import RunVersionChips from '@/components/analysis/runs/RunVersionChips.vue';
import { sourceLabel, sourceRoute } from '@/components/queue/queueFormat';
import { isTerminalRunStatus, normalizeRunStatus } from '@/store/modules/analysisQueue';
import { runStatusPresentation } from '@/utils/runLabels';

const LIVE_STATUSES = ['QUEUED', 'DISPATCHED', 'RUNNING'];

/**
 * Runs of an analysis group grouped by batch (M4 contract 8, `/:id/runs` + `/:id/batches`):
 * per batch the counts by status, whether it is complete and its automatic snapshot; per run the
 * status chip, version chips, result, timing and Open / Cancel / Pin actions.
 * Emits `cancel` (run), `pin` (run), `run-group`.
 */
export default {
  name: 'GroupRunsTable',

  components: { RunStatusChip, RunVersionChips },

  props: {
    runs: { type: Array, default: () => [] },
    /** `[{ batch, started_at, counts_by_status, complete, snapshot_id }]` (optional). */
    batchSummaries: { type: Array, default: () => [] },
    /** Pinned run ids of the members (to show the pin state). */
    pinnedRunIds: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },

  data() {
    return { openPanels: [0] };
  },

  computed: {
    batches() {
      const byBatch = new Map();
      this.runs.forEach((run) => {
        const key = run.batch || '';
        if (!byBatch.has(key)) byBatch.set(key, []);
        byBatch.get(key).push(run);
      });
      const summaries = new Map(this.batchSummaries.map((item) => [item.batch || '', item]));
      const batches = Array.from(byBatch.entries()).map(([batch, runs]) => {
        const summary = summaries.get(batch) || {};
        const counts = {};
        runs.forEach((run) => {
          const status = normalizeRunStatus(run.status) || 'UNKNOWN';
          counts[status] = (counts[status] || 0) + 1;
        });
        const startedAt =
          summary.started_at ||
          runs
            .map((run) => run.enqueued_at || run.started_at)
            .filter(Boolean)
            .sort()[0] ||
          null;
        return {
          batch,
          started_at: startedAt,
          complete: summary.complete ?? runs.every((run) => isTerminalRunStatus(run.status)),
          snapshot_id: summary.snapshot_id || null,
          status_counts: Object.keys(counts).map((status) => ({
            status,
            n: counts[status],
            label: runStatusPresentation(status).label,
            color: runStatusPresentation(status).color,
          })),
          runs: [...runs].sort((a, b) => String(a.source_id).localeCompare(String(b.source_id))),
        };
      });
      return batches.sort((a, b) =>
        String(b.started_at || '').localeCompare(String(a.started_at || ''))
      );
    },
  },

  methods: {
    sourceLabel,
    sourceRoute,

    runIdOf(run) {
      return run._id || run.run_id;
    },

    isLive(run) {
      return LIVE_STATUSES.includes(normalizeRunStatus(run.status));
    },

    isQueued(run) {
      return normalizeRunStatus(run.status) === 'QUEUED';
    },

    isSucceeded(run) {
      return normalizeRunStatus(run.status) === 'SUCCEEDED';
    },

    isPinned(run) {
      return this.pinnedRunIds.map(String).includes(String(this.runIdOf(run)));
    },

    resultOf(run) {
      const summary = run.result_summary || run.result || null;
      if (!summary || typeof summary !== 'object') return '—';
      const parts = [];
      const match = summary.global_match_pct ?? summary.global_match ?? summary.match_pct;
      if (match != null) parts.push(`${match}%`);
      const alerts = summary.alerts ?? summary.alerts_total;
      if (alerts != null) parts.push(`${alerts} alerts`);
      return parts.length ? parts.join(' · ') : '—';
    },

    durationOf(run) {
      if (!run.started_at || !run.finished_at) return '—';
      const ms = new Date(run.finished_at).getTime() - new Date(run.started_at).getTime();
      if (!Number.isFinite(ms) || ms < 0) return '—';
      const total = Math.round(ms / 1000);
      const m = Math.floor(total / 60);
      const s = total % 60;
      return m ? `${m} m ${String(s).padStart(2, '0')} s` : `${s} s`;
    },

    formatDate(value) {
      if (!value) return '—';
      const date = new Date(value);
      if (!Number.isFinite(date.getTime())) return String(value);
      const pad = (n) => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
        date.getHours()
      )}:${pad(date.getMinutes())}`;
    },

    openRun(run) {
      const base = sourceRoute(run);
      if (!base) return;
      const id = this.runIdOf(run);
      const route = id ? `${base}?run=${encodeURIComponent(id)}` : base;
      this.$emit('open', run);
      if (this.$router) this.$router.push(route);
    },
  },
};
</script>

<style scoped>
.group-runs-table__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 16px;
  text-align: center;
  border-radius: 10px !important;
}
.group-runs-table__batches .v-expansion-panel {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px !important;
  margin-bottom: 8px;
}
.group-runs-table__batch-title {
  gap: 2px;
}
.group-runs-table__table {
  background: transparent !important;
}
.group-runs-table__source {
  font-weight: 600;
}
</style>
